import { useCallback, useEffect, useMemo, useRef } from "react";
import { AgGrid, Grid, Text } from "@jsq/ds/core";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { NoMatchesOverlay } from "./NoMatchesOverlay";
import { ancestorOnlyText } from "./styles_vanilla.css";

/** A flattened tree row addressable by a stable `id` built from its ancestor
 *  `path` (root-first path segments) via {@link toTreeRowId}. */
export type TreeRow = {
  id: string;
  path: string[];
};

/** Builds a row id from a path. Segments are percent-encoded because a
 *  hierarchy name or code may itself contain the "/" separator — without
 *  encoding, a node called "Fees/Expenses" and a child "Expenses" under a
 *  parent "Fees" would produce the same id. */
export const toTreeRowId = (path: readonly string[]): string =>
  path.map(encodeURIComponent).join("/");

/**
 * Filter flattened tree rows to those matching `searchTerm`, keeping every
 * ancestor of a match so a tree grid can still render the full path to each
 * hit. Rows that survive only because a descendant matched are flagged
 * `isAncestorOnly: true` so callers can render them muted / un-highlighted.
 * An empty (or whitespace) search returns every row with `isAncestorOnly:
 * false`.
 *
 * `matches` receives the row plus a trimmed, lower-cased needle, so callers
 * don't have to normalize the search term themselves.
 */
export const filterTreeRowsWithAncestors = <T extends TreeRow>(
  rows: ReadonlyArray<T>,
  searchTerm: string,
  matches: (row: T, normalizedSearch: string) => boolean,
): Array<T & { isAncestorOnly: boolean }> => {
  if (!searchTerm.trim()) {
    return rows.map((row) => ({ ...row, isAncestorOnly: false }));
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchingIds = new Set<string>();
  rows.forEach((row) => {
    if (matches(row, normalizedSearch)) matchingIds.add(row.id);
  });

  const pathsToInclude = new Set<string>();
  rows.forEach((row) => {
    if (!matchingIds.has(row.id)) return;
    for (let i = 1; i <= row.path.length; i++) {
      pathsToInclude.add(toTreeRowId(row.path.slice(0, i)));
    }
  });

  return rows
    .filter((row) => pathsToInclude.has(row.id))
    .map((row) => ({ ...row, isAncestorOnly: !matchingIds.has(row.id) }));
};

export type HierarchyNodeData = {
  id?: number;
  name: string;
  code: string;
  description?: string;
  is_leaf?: boolean;
  transaction_type_id?: number;
  children?: HierarchyNodeData[];
};

export type TypeHierarchy = {
  tree_structure?: {
    root?: HierarchyNodeData;
  };
};

export type FlattenedNode = {
  id: string;
  name: string;
  code: string;
  description?: string;
  path: string[];
  isAncestorOnly?: boolean;
  /** Numeric hierarchy-node id; absent on nodes not yet persisted. */
  nodeId?: number;
  /** Ancestor names (root first, excluding this node). */
  namePath?: string[];
};

type HierarchyTreeGridProps = {
  typeHierarchy: TypeHierarchy | null;
  searchValue?: string;
};

type GridContext = {
  searchValue: string;
};

const HighlightCellRenderer = (
  params: AgGrid.ICellRendererParams<FlattenedNode, string, GridContext>,
) => {
  const searchValue = params.context?.searchValue ?? "";
  const isAncestorOnly = params.data?.isAncestorOnly === true;
  const value = params.value?.trim();

  if (!value) {
    return <Text variant="secondary-sm">-</Text>;
  }

  return (
    <Text variant="regular-md" className={isAncestorOnly ? ancestorOnlyText : undefined} asSpan>
      <HighlightText text={value} searchValue={isAncestorOnly ? "" : searchValue} />
    </Text>
  );
};

const HighlightInnerRenderer = (
  params: AgGrid.ICellRendererParams<FlattenedNode, string, GridContext>,
) => {
  const searchValue = params.context?.searchValue ?? "";
  const value = params.data?.name ?? "";
  const isAncestorOnly = params.data?.isAncestorOnly === true;
  return (
    <Text variant="regular-md" className={isAncestorOnly ? ancestorOnlyText : undefined} asSpan>
      <HighlightText text={value} searchValue={isAncestorOnly ? "" : searchValue} />
    </Text>
  );
};

const flattenHierarchy = (
  node: HierarchyNodeData,
  parentPath: string[] = [],
): FlattenedNode[] => {
  const currentPath = [...parentPath, node.code];
  const currentNode: FlattenedNode = {
    id: toTreeRowId(currentPath),
    name: node.name,
    code: node.code,
    description: node.description,
    path: currentPath,
  };

  const childNodes =
    node.children?.flatMap((child) => flattenHierarchy(child, currentPath)) ?? [];

  return [currentNode, ...childNodes];
};

const nodeMatchesSearch = (node: FlattenedNode, normalizedSearch: string): boolean =>
  node.name.toLowerCase().includes(normalizedSearch) ||
  node.code.toLowerCase().includes(normalizedSearch);

export const HierarchyTreeGrid = ({
  typeHierarchy,
  searchValue = "",
}: HierarchyTreeGridProps) => {
  const allNodes = useMemo<FlattenedNode[]>(() => {
    if (!typeHierarchy) return [];

    const root = typeHierarchy.tree_structure?.root;
    if (root) {
      return flattenHierarchy(root);
    }

    return [];
  }, [typeHierarchy]);

  const rowData = useMemo<FlattenedNode[]>(
    () => filterTreeRowsWithAncestors(allNodes, searchValue, nodeMatchesSearch),
    [allNodes, searchValue],
  );

  const groupDefaultExpanded = useMemo(() => {
    return searchValue.trim() ? -1 : 1;
  }, [searchValue]);

  const columnDefs = useMemo<AgGrid.ColDef<FlattenedNode>[]>(
    () => [
      {
        field: "description",
        headerName: "Description",
        sortable: false,
        flex: 2,
        cellRenderer: HighlightCellRenderer,
      },
    ],
    [],
  );

  const autoGroupColumnDef = useMemo<AgGrid.ColDef<FlattenedNode>>(
    () => ({
      headerName: "Node name",
      field: "name",
      sortable: false,
      flex: 1,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: HighlightInnerRenderer,
      },
    }),
    [],
  );

  const context = useMemo<GridContext>(
    () => ({
      searchValue,
    }),
    [searchValue],
  );

  const gridRef = useRef<AgGrid.GridApi<FlattenedNode> | null>(null);

  const handleGridReady = useCallback((params: AgGrid.GridReadyEvent<FlattenedNode>) => {
    gridRef.current = params.api;
  }, []);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.refreshCells({ force: true });
    }
  }, [searchValue]);

  const getDataPath = useCallback((data: FlattenedNode): string[] => {
    return data.path;
  }, []);

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      domLayout: "autoHeight",
      rowHeight: 50,
      suppressContextMenu: true,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
    },
  });

  return (
    <Grid<FlattenedNode>
      rowData={rowData}
      getRowId={(params) => params.data?.id ?? ""}
      columnDefs={columnDefs}
      treeData
      getDataPath={getDataPath}
      autoGroupColumnDef={autoGroupColumnDef}
      groupDefaultExpanded={groupDefaultExpanded}
      gridOptions={gridOptions}
      context={context}
      onGridReady={handleGridReady}
      reactiveCustomComponents
      noRowsOverlayComponent={NoMatchesOverlay}
    />
  );
};
